import { currentBusinessModeLabel } from './businessModes';
import {
  assertValidCalculationDefinition,
  type CalculationEvaluationResult,
  evaluateCalculation,
  evaluateSystemCalculationWithFactors,
} from './fieldCalculations';
import {
  quoteFieldAvailabilityForItem,
  quoteFieldAvailabilityLabel,
} from './quoteFieldAvailability';
import {
  quoteLocalDynamicCost,
  quoteLocalDynamicCostLine,
  quoteLocalExclusiveDiscount,
  quoteLocalExclusiveLineCents,
  quoteLocalPricing,
  quoteLocalRentalPrice,
  quoteLocalRetailPriceTax13,
} from './quoteLocalPricing';
import { quoteDiscountParticipation } from './quotePackageGroupPolicy';
import { quotePackageGroupItems } from './quoteTableGroups';
import {
  dynamicCostSubtotal,
  lineTotal,
  packageDiscountedQuote,
  packageDiscountedUnitPrice,
  packageItemMonthlySubtotal,
  planProductPrice,
  planProductSubtotal,
  purchaseFinalPriceExTax13,
  purchaseFinalPriceTax13,
  quoteMonthlySubtotal,
  rentalLineTotal,
} from './quoteTotals';
import type {
  CalculationOperandValueType,
  CpqState,
  CustomFieldOwner,
  DeviceCatalogRecord,
  FrozenCalculationFactorFact,
  Quote,
  QuoteColumnSnapshot,
  QuoteFieldDefinitionRecord,
  QuoteFieldPlacement,
  QuoteItem,
} from './types';
import type { UnifiedFieldValuePort } from './unifiedFieldPort';
import {
  createUnifiedFieldRegistry,
  type UnifiedFieldDescriptor,
} from './unifiedFieldRegistry';

export interface UnifiedFieldResolutionContext {
  owner: CustomFieldOwner;
  entityId: string;
  /** Quote-line resolution must receive the exact quote version explicitly. */
  quoteId?: string;
  quoteVersion?: number;
  relationId?: string;
  scopeId?: string;
  item?: QuoteItem;
  /** Runtime-injected value boundary for enterprise fields. */
  fieldValuePort?: UnifiedFieldValuePort;
}

export interface ResolvedTypedFieldValue {
  key: string;
  type: QuoteFieldDefinitionRecord['type'];
  status: 'value' | 'missing' | 'error';
  value?: string | number | boolean | string[];
  source?: 'system' | 'enterprise' | 'calculated';
  missingFieldKeys?: string[];
  error?: string;
  definitionVersion?: string;
  factorFacts?: FrozenCalculationFactorFact[];
}

export interface QuoteFieldResolutionRuntime {
  readonly definitions: QuoteFieldDefinitionRecord[];
  readonly descriptors: readonly UnifiedFieldDescriptor[];
  readonly descriptorsByKey: ReadonlyMap<string, UnifiedFieldDescriptor>;
  readonly registryError?: string;
}

/** Compile registry metadata once for an explicit resolution batch. */
export function createQuoteFieldResolutionRuntime(
  state: CpqState,
): QuoteFieldResolutionRuntime {
  try {
    const descriptors = createUnifiedFieldRegistry(
      state.catalog.quoteFieldDefinitions,
    );
    return {
      definitions: state.catalog.quoteFieldDefinitions,
      descriptors,
      descriptorsByKey: new Map(
        descriptors.map((descriptor) => [
          descriptor.definition.key,
          descriptor,
        ]),
      ),
    };
  } catch (error) {
    return {
      definitions: state.catalog.quoteFieldDefinitions,
      descriptors: [],
      descriptorsByKey: new Map(),
      registryError:
        error instanceof Error ? error.message : '统一字段注册表无效',
    };
  }
}

function currentResolutionRuntime(
  state: CpqState,
  runtime?: QuoteFieldResolutionRuntime,
): QuoteFieldResolutionRuntime | undefined {
  return runtime?.definitions === state.catalog.quoteFieldDefinitions
    ? runtime
    : undefined;
}

function calculationType(
  type: QuoteFieldDefinitionRecord['type'],
): CalculationOperandValueType | undefined {
  return type === 'number' || type === 'money' || type === 'percent'
    ? type
    : undefined;
}

function quoteLineForContext(
  state: CpqState,
  context: UnifiedFieldResolutionContext,
): { quote: Quote; item: QuoteItem } | undefined {
  if (context.owner !== 'quote-line') return undefined;
  if (!context.quoteId) return undefined;
  const quote = state.quotes[context.quoteId];
  const item = quote?.items.find(
    (candidate) => candidate.itemId === context.entityId,
  );
  return quote && item ? { quote, item } : undefined;
}

function explicitSystemValue(
  state: CpqState,
  key: string,
  context: UnifiedFieldResolutionContext,
): string | number | boolean | string[] | undefined {
  if (context.owner === 'device') {
    const record = state.catalog.devices.find(
      (candidate) => candidate.id === context.entityId,
    );
    if (!record) return undefined;
    const values: Record<
      string,
      string | number | boolean | string[] | undefined
    > = {
      device_name: record.name,
      device_sku: record.sku ?? undefined,
      jky_code: record.jkyCode,
      device_category: record.category,
      device_attribute: record.deviceAttribute,
      procurement_attribute: record.procurementAttribute,
      device_weight: record.weight ?? Number.NaN,
      device_status: record.status,
      device_internal_remark: record.remark,
      rental_price: record.rentalPrice ?? undefined,
      tax_inclusive_plan_price: record.taxInclusivePlanPrice ?? NaN,
      device_unit_price: record.unitPrice ?? undefined,
      device_untaxed_plan_price: record.untaxedPlanPrice ?? undefined,
      retail_price_tax13: record.retailPriceTax13 ?? undefined,
      purchase_final_price_tax13: record.buyoutFinalPriceTax13,
      purchase_final_price_ex_tax13: record.purchaseFinalPriceExTax13 ?? NaN,
      dynamic_cost: record.dynamicCost ?? undefined,
      device_compensation_price: record.compensationPrice ?? undefined,
      device_image: record.image,
      replacement_device: record.replacementIds,
      warehouse_stock: Object.values(record.stockByWarehouse).reduce(
        (sum, value) => sum + value,
        0,
      ),
    };
    return values[key];
  }
  if (context.owner === 'package') {
    const record = state.catalog.packages.find(
      (candidate) => candidate.id === context.entityId,
    );
    if (!record) return undefined;
    if (key === 'package_name') return record.name;
    if (key === 'package_plan_ids') return record.planIds;
    if (key === 'package_image') return record.image;
    if (key === 'package_light_image') return record.lightImage;
    if (key === 'package_product_links')
      return record.productLinks.map((link) => link.deviceId);
    const relation = context.relationId
      ? record.productLinks.find(
          (link) => link.relationId === context.relationId,
        )
      : undefined;
    if (key === 'package_relation_quantity') return relation?.quantity;
    if (key === 'package_item_price')
      return relation?.packageItemPrice ?? undefined;
    if (key === 'package_item_monthly_subtotal')
      return relation
        ? relation.itemMonthlySubtotalCents == null
          ? packageItemMonthlySubtotal(relation)
          : relation.itemMonthlySubtotalCents / 100
        : undefined;
    return undefined;
  }
  if (context.owner === 'plan') {
    const record = state.catalog.plans.find(
      (candidate) => candidate.id === context.entityId,
    );
    if (!record) return undefined;
    const values: Record<string, string | number | string[]> = {
      plan_name: record.name,
      scheme_discount: record.discount,
      plan_image: record.image,
      plan_cases: record.cases,
      plan_package_ids: state.catalog.packages
        .filter((pkg) => pkg.planIds.includes(record.id))
        .map((pkg) => pkg.id),
    };
    return values[key];
  }
  if (context.owner === 'customer') {
    const record = state.catalog.customers.find(
      (candidate) => candidate.id === context.entityId,
    );
    if (!record) return undefined;
    const values: Record<string, string> = {
      customer_no: record.customerNo,
      customer_name: record.name,
      customer_contact: record.contact,
      customer_phone: record.phone,
      customer_manager: record.manager,
      customer_status: record.status,
    };
    return values[key];
  }
  if (context.owner === 'quote-header') {
    const quote = state.quotes[context.entityId];
    if (!quote) return undefined;
    const values: Record<string, string | number> = {
      quote_title: quote.title,
      customer_name: quote.customer.name,
      customer_contact: quote.customer.contact,
      customer_phone: quote.customer.phone,
      valid_until: quote.validUntil,
      business_mode: quote.businessModeCode,
      deposit: quote.deposit,
      overall_discount: quote.overallDiscount,
    };
    return values[key];
  }
  const line = quoteLineForContext(state, context);
  if (!line) return undefined;
  const { quote, item } = line;
  const snapshot = item.deviceFieldSnapshot;
  const group = quotePackageGroupItems(quote, item);
  const planDiscount = quotePlanDisplayDiscount(item);
  const values: Record<string, string | number | string[]> = {
    device_id: item.deviceId,
    device_name: item.quoteDisplayName?.trim() || item.name,
    device_sku: item.sku,
    source_type: item.sourceType,
    source_id: item.sourceId,
    source_name: item.sourceName,
    quantity: item.quantity,
    item_discount: item.discount,
    stock: item.stock,
    replacement_device: item.replacementDeviceId ?? '',
    device_attribute: snapshot?.deviceAttribute ?? '',
    remark: item.remark ?? '',
    rental_price: snapshot?.rentalPrice ?? NaN,
    tax_inclusive_plan_price: snapshot?.taxInclusivePlanPrice ?? NaN,
    device_unit_price: snapshot?.unitPrice ?? NaN,
    device_untaxed_plan_price: snapshot?.untaxedPlanPrice ?? NaN,
    device_compensation_price: snapshot?.compensationPrice ?? NaN,
    package_item_price: item.packageItemPriceSnapshot ?? NaN,
    package_item_monthly_subtotal:
      item.packageItemMonthlySubtotalSnapshot !== undefined &&
      item.packageRelationQuantitySnapshot !== undefined &&
      item.quantity % item.packageRelationQuantitySnapshot === 0
        ? item.packageItemMonthlySubtotalSnapshot *
          (item.quantity / item.packageRelationQuantitySnapshot)
        : item.packageItemPriceSnapshot !== undefined &&
            item.packageRelationQuantitySnapshot !== undefined
          ? packageItemMonthlySubtotal({
              packageItemPrice: item.packageItemPriceSnapshot,
              quantity: item.packageRelationQuantitySnapshot,
            })
          : NaN,
    package_relation_quantity: item.packageRelationQuantitySnapshot ?? NaN,
    current_pricing_base: item.pricingBaseSnapshot?.amount ?? NaN,
    scheme_discount: planDiscount ?? NaN,
    scheme_product_price: quotePlanDisplayUnitPrice(item, planDiscount) ?? NaN,
    scheme_product_subtotal:
      quotePlanDisplaySubtotal(item, planDiscount) ?? NaN,
    // Retired field identity: use a current scoped subtotal only when reading
    // an old frozen projection that still names this key.
    scheme_package_price:
      item.planSubtotalSnapshot ?? item.planPackagePriceSnapshot ?? NaN,
    // Retired identity: only previously frozen records may read this alias.
    package_original_price_tax13:
      item.packageMonthlySubtotalSnapshot ??
      item.packageOriginalPriceSnapshot ??
      NaN,
    package_discount_quote_tax13: item.packageDiscountedQuoteSnapshot ?? NaN,
    package_discounted_unit_price:
      item.sourceFragment ||
      item.formalPlanOccurrence ||
      hasAboveBasisFrozenPlanPrice(item)
        ? lineTotal(item) / item.quantity
        : item.packageItemPriceSnapshot !== undefined
          ? packageDiscountedUnitPrice(
              item.packageItemPriceSnapshot,
              item.planId ? (item.planDiscountSnapshot ?? 100) : 100,
              item.discount,
            )
          : NaN,
    package_discounted_subtotal: rentalLineTotal(item, quote.businessModeCode),
    product_final_discount_price: lineTotal(item),
    rental_subtotal:
      snapshot?.rentalPrice != null
        ? quoteMonthlySubtotal(snapshot.rentalPrice, item.quantity)
        : NaN,
    unit_price_subtotal:
      snapshot?.unitPrice != null ? snapshot.unitPrice * item.quantity : NaN,
    dynamic_cost: snapshot?.dynamicCost ?? NaN,
    dynamic_cost_subtotal:
      snapshot?.dynamicCost != null && Number.isFinite(snapshot.dynamicCost)
        ? dynamicCostSubtotal(snapshot.dynamicCost, item.quantity)
        : NaN,
    retail_price_tax13: snapshot?.retailPriceTax13 ?? NaN,
    purchase_final_price_tax13: purchaseFinalPriceTax13(
      item,
      quote.businessModeCode,
    ),
    purchase_final_price_ex_tax13: purchaseFinalPriceExTax13(
      item,
      quote.businessModeCode,
    ),
    warehouse_stock: snapshot
      ? Object.values(snapshot.stockByWarehouse ?? {}).reduce(
          (sum, value) => sum + value,
          0,
        )
      : NaN,
    valid_until: quote.validUntil,
  };
  void group;
  return values[key];
}

function systemCalculationContext(
  state: CpqState,
  definition: QuoteFieldDefinitionRecord,
  context: UnifiedFieldResolutionContext,
) {
  const line = quoteLineForContext(state, context);
  if (line) {
    const { quote, item } = line;
    const packageItems = quotePackageGroupItems(quote, item);
    const audit =
      context.quoteId && context.quoteVersion !== undefined
        ? {
            quoteId: context.quoteId,
            quoteVersion: context.quoteVersion,
            owner: context.owner,
            scopeId: context.scopeId ?? context.entityId,
            itemId: item.itemId,
            relationId: context.relationId ?? item.packageRelationId,
          }
        : undefined;
    return {
      rentalPrice:
        // 小计租金的批准来源是报价行已冻结的设备租金。不能借用
        // "当前计价基数"的 rental-only resolver，否则销售/买断报价也会
        // 错误地把这个系统字段解析成缺失。
        item.deviceFieldSnapshot?.rentalPrice ?? undefined,
      quantity: item.quantity,
      currentPricingBase: item.pricingBaseSnapshot?.amount,
      planDiscount:
        definition.key === 'product_final_discount_price' &&
        item.pricingBaseSnapshot?.kind === 'buyout-tax13'
          ? 100
          : item.planId
            ? (item.planDiscountSnapshot ?? 100)
            : 100,
      itemDiscount: item.discount,
      dynamicCost: item.deviceFieldSnapshot?.dynamicCost ?? undefined,
      deviceUnitPrice: item.deviceFieldSnapshot?.unitPrice ?? undefined,
      packageItemPrice: item.packageItemPriceSnapshot,
      packageRelations: packageItems.map((candidate) => ({
        relationId: candidate.packageRelationId,
        itemId: candidate.itemId,
        packageItemPrice: candidate.packageItemPriceSnapshot ?? NaN,
        quantity: candidate.packageRelationQuantitySnapshot ?? NaN,
      })),
      pricedLines: packageItems.map((candidate) => ({
        itemId: candidate.itemId,
        relationId: candidate.packageRelationId,
        pricingBaseAmount: candidate.pricingBaseSnapshot?.amount ?? NaN,
        planDiscount: candidate.planId
          ? (candidate.planDiscountSnapshot ?? 100)
          : 100,
        itemDiscount: candidate.discount,
        quantity: candidate.quantity,
      })),
      audit,
    };
  }
  if (context.owner === 'package') {
    const record = state.catalog.packages.find(
      (candidate) => candidate.id === context.entityId,
    );
    return {
      packageRelations: record?.productLinks.map((relation) => ({
        relationId: relation.relationId,
        packageItemPrice: relation.packageItemPrice,
        quantity: relation.quantity,
      })),
      packageItemPrice: explicitSystemValue(
        state,
        'package_item_price',
        context,
      ) as number | undefined,
      planDiscount: 100,
      audit:
        context.quoteId && context.quoteVersion !== undefined
          ? {
              quoteId: context.quoteId,
              quoteVersion: context.quoteVersion,
              owner: context.owner,
              scopeId: context.scopeId ?? context.entityId,
              itemId: context.item?.itemId,
              relationId: context.relationId,
            }
          : undefined,
    };
  }
  if (context.owner === 'plan') {
    const plan = state.catalog.plans.find(
      (candidate) => candidate.id === context.entityId,
    );
    const packageRelations = state.catalog.packages
      .filter((pkg) => plan && pkg.planIds.includes(plan.id))
      .flatMap((pkg) => pkg.productLinks);
    return {
      deviceUnitPrice:
        context.item?.deviceFieldSnapshot?.unitPrice ?? undefined,
      quantity:
        context.item?.packageRelationQuantitySnapshot ?? context.item?.quantity,
      packageRelations: packageRelations.map((relation) => ({
        relationId: relation.relationId,
        packageItemPrice: relation.packageItemPrice,
        quantity: relation.quantity,
      })),
      packageItemPrice: explicitSystemValue(
        state,
        'package_item_price',
        context,
      ) as number | undefined,
      planDiscount: plan?.discount,
      audit:
        context.quoteId && context.quoteVersion !== undefined
          ? {
              quoteId: context.quoteId,
              quoteVersion: context.quoteVersion,
              owner: context.owner,
              scopeId: context.scopeId ?? context.entityId,
              itemId: context.item?.itemId,
              relationId: context.relationId,
            }
          : undefined,
    };
  }
  void definition;
  return undefined;
}

function errorResult(
  field: QuoteFieldDefinitionRecord,
  error: string,
  missingFieldKeys?: string[],
): ResolvedTypedFieldValue {
  return {
    key: field.key,
    type: field.type,
    status: 'error',
    error,
    ...(missingFieldKeys ? { missingFieldKeys } : {}),
    definitionVersion: field.governance.definitionVersion,
  };
}

function quoteLineRentalSnapshotValue(
  state: CpqState,
  field: QuoteFieldDefinitionRecord,
  context: UnifiedFieldResolutionContext,
): ResolvedTypedFieldValue | undefined {
  if (context.owner !== 'quote-line' || field.key !== 'rental_price')
    return undefined;
  if (!context.quoteId || context.quoteVersion === undefined)
    return errorResult(field, '历史租金必须绑定明确报价版本');
  const quote = state.quotes[context.quoteId];
  if (!quote || quote.version !== context.quoteVersion)
    return errorResult(field, '历史租金报价版本不匹配');
  const item = quote.items.find(
    (candidate) => candidate.itemId === context.entityId,
  );
  if (!item || (context.item && context.item.itemId !== item.itemId))
    return errorResult(field, '历史租金报价行不属于当前报价版本');
  const pricing = item.pricingBaseSnapshot;
  if (
    pricing?.kind !== 'rental' ||
    pricing.calculationVersion !== 'pricing-v2' ||
    !Number.isFinite(pricing.amount)
  )
    return errorResult(field, '历史租金计价快照无效');
  const deviceRentalPrice = item.deviceFieldSnapshot?.rentalPrice;
  if (
    deviceRentalPrice !== undefined &&
    (!Number.isFinite(deviceRentalPrice) ||
      deviceRentalPrice !== pricing.amount)
  )
    return errorResult(field, '历史租金冻结事实冲突');
  return {
    key: field.key,
    type: field.type,
    status: 'value',
    value: pricing.amount,
    source: 'system',
    definitionVersion: field.governance.definitionVersion,
  };
}

function quoteLineLocalPriceValue(
  state: CpqState,
  field: QuoteFieldDefinitionRecord,
  context: UnifiedFieldResolutionContext,
): ResolvedTypedFieldValue | undefined {
  const line = quoteLineForContext(state, context);
  if (!line) return undefined;
  if (line.quote.status !== 'draft') {
    if (
      field.key === 'package_discounted_subtotal' &&
      Number.isSafeInteger(line.item.formalRentalLineTotalCents)
    )
      return {
        key: field.key,
        type: field.type,
        status: 'value',
        value: (line.item.formalRentalLineTotalCents as number) / 100,
        source: 'system',
        definitionVersion: field.governance.definitionVersion,
      };
    if (
      (field.key === 'package_discounted_subtotal' ||
        field.key === 'product_final_discount_price') &&
      Number.isSafeInteger(line.item.formalLineTotalCents) &&
      (line.item.formalLineTotalCents as number) >= 0
    ) {
      return {
        key: field.key,
        type: field.type,
        status: 'value',
        value: (line.item.formalLineTotalCents as number) / 100,
        source: 'system',
        definitionVersion: field.governance.definitionVersion,
      };
    }
    return undefined;
  }
  const { item } = line;
  const local = quoteLocalPricing(item);
  let value: number | undefined;
  switch (field.key) {
    case 'rental_price':
      if (local.rentalPriceCents !== undefined)
        value = quoteLocalRentalPrice(item);
      break;
    case 'rental_subtotal':
      if (local.rentalPriceCents !== undefined) {
        const unit = quoteLocalRentalPrice(item);
        value = unit === undefined ? undefined : unit * item.quantity;
      }
      break;
    case 'dynamic_cost':
      if (local.dynamicCostCents !== undefined)
        value = quoteLocalDynamicCost(item);
      break;
    case 'dynamic_cost_subtotal':
      if (
        local.dynamicCostCents !== undefined ||
        local.dynamicCostLineCents !== undefined
      )
        value = quoteLocalDynamicCostLine(item);
      break;
    case 'retail_price_tax13':
      if (local.retailPriceTax13Cents !== undefined)
        value = quoteLocalRetailPriceTax13(item);
      break;
    case 'package_discounted_subtotal':
    case 'product_final_discount_price':
      if (local.exclusiveLineCents !== undefined)
        value =
          field.key === 'package_discounted_subtotal'
            ? rentalLineTotal(item, line.quote.businessModeCode)
            : lineTotal(item);
      break;
  }
  return value === undefined || !Number.isFinite(value)
    ? undefined
    : {
        key: field.key,
        type: field.type,
        status: 'value',
        value,
        source: 'system',
        definitionVersion: field.governance.definitionVersion,
      };
}

/** Single typed resolver used by object pages, calculation inputs, quote
 * snapshots and channel projections. It never searches by label or falls
 * back to a different entity/version. */
export function resolveUnifiedFieldValue(
  state: CpqState,
  fieldKey: string,
  context: UnifiedFieldResolutionContext,
  stack: readonly string[] = [],
  resolutionRuntime?: QuoteFieldResolutionRuntime,
): ResolvedTypedFieldValue {
  let runtime = currentResolutionRuntime(state, resolutionRuntime);
  let field = state.catalog.quoteFieldDefinitions.find(
    (candidate) => candidate.key === fieldKey,
  );
  try {
    if (!field) {
      runtime ??= createQuoteFieldResolutionRuntime(state);
      if (runtime.registryError) throw new Error(runtime.registryError);
      field = runtime.descriptorsByKey.get(fieldKey)?.definition;
    }
  } catch (error) {
    return {
      key: fieldKey,
      type: 'number',
      status: 'error',
      error: error instanceof Error ? error.message : '统一字段注册表无效',
    };
  }
  if (!field) {
    return {
      key: fieldKey,
      type: 'number',
      status: 'error',
      error: `字段定义不存在：${fieldKey}`,
    };
  }
  if (stack.includes(fieldKey)) return errorResult(field, '计算依赖循环');
  if (
    context.owner === 'quote-line' &&
    (!context.quoteId || context.quoteVersion === undefined)
  )
    return errorResult(field, '报价行必须绑定明确报价版本');
  if (
    context.owner === 'quote-line' &&
    state.quotes[context.quoteId as string]?.version !== context.quoteVersion
  )
    return errorResult(field, '报价行版本不匹配');
  if (context.owner === 'quote-line' && !quoteLineForContext(state, context))
    return errorResult(field, '报价行不存在或不属于当前报价版本');
  if (
    context.owner === 'package' &&
    !state.catalog.packages.some(
      (candidate) => candidate.id === context.entityId,
    )
  )
    return errorResult(field, '套餐不存在或不属于当前计算范围');
  if (
    context.owner === 'plan' &&
    !state.catalog.plans.some((candidate) => candidate.id === context.entityId)
  )
    return errorResult(field, '方案不存在或不属于当前计算范围');
  if (
    context.owner === 'device' &&
    !state.catalog.devices.some(
      (candidate) => candidate.id === context.entityId,
    )
  )
    return errorResult(field, '设备不存在或不属于当前计算范围');
  if (
    context.owner === 'customer' &&
    !state.catalog.customers.some(
      (candidate) => candidate.id === context.entityId,
    )
  )
    return errorResult(field, '客户不存在或不属于当前计算范围');
  const localPrice = quoteLineLocalPriceValue(state, field, context);
  if (localPrice) return localPrice;
  if (
    context.owner === 'quote-line' &&
    fieldKey === 'package_discounted_subtotal'
  ) {
    const line = quoteLineForContext(state, context);
    if (line?.item.sourceType === 'device') {
      const value = rentalLineTotal(line.item);
      return Number.isFinite(value)
        ? {
            key: field.key,
            type: field.type,
            status: 'value',
            value,
            source: 'system',
            definitionVersion: field.governance.definitionVersion,
          }
        : errorResult(field, '设备最终报价无效');
    }
  }
  const frozenPlanLine = quoteLineForContext(state, context)?.item;
  if (frozenPlanLine && hasFrozenPlanPrice(frozenPlanLine)) {
    const discount = quotePlanDisplayDiscount(frozenPlanLine);
    const frozenValue =
      field.key === 'scheme_product_price'
        ? quotePlanDisplayUnitPrice(frozenPlanLine, discount)
        : field.key === 'scheme_product_subtotal'
          ? quotePlanDisplaySubtotal(frozenPlanLine, discount)
          : field.key === 'scheme_discount'
            ? discount
            : field.key === 'package_discounted_unit_price' &&
                hasAboveBasisFrozenPlanPrice(frozenPlanLine)
              ? lineTotal(frozenPlanLine) / frozenPlanLine.quantity
              : undefined;
    if (typeof frozenValue === 'number' && Number.isFinite(frozenValue))
      return {
        key: field.key,
        type: field.type,
        status: 'value',
        value: frozenValue,
        source: 'system',
        definitionVersion: field.governance.definitionVersion,
      };
  }
  const historicalRental = quoteLineRentalSnapshotValue(state, field, context);
  if (historicalRental) return historicalRental;
  if (
    context.owner === 'package' &&
    (fieldKey === 'package_relation_quantity' ||
      fieldKey === 'package_item_price')
  ) {
    if (!context.relationId)
      return errorResult(field, '套餐关系字段必须绑定明确关系标识');
    const pkg = state.catalog.packages.find(
      (candidate) => candidate.id === context.entityId,
    );
    if (
      !pkg?.productLinks.some(
        (relation) => relation.relationId === context.relationId,
      )
    )
      return errorResult(field, '套餐关系不存在或不属于当前套餐');
  }
  const frozenQuote = context.quoteId
    ? state.quotes[context.quoteId]
    : undefined;
  const frozenItem =
    context.item ??
    frozenQuote?.items.find((item) => item.itemId === context.entityId);
  const frozenCalculation = frozenItem?.calculationSnapshots?.[fieldKey];
  if (
    field.calculation &&
    field.governance.kind === 'custom' &&
    frozenQuote?.status !== 'draft' &&
    frozenCalculation
  ) {
    return {
      key: field.key,
      type: field.type,
      status: 'value',
      value: frozenCalculation.result,
      source: 'calculated',
      definitionVersion: frozenCalculation.definitionVersion,
    };
  }
  if (field.calculation && field.governance.kind === 'custom') {
    try {
      runtime ??= createQuoteFieldResolutionRuntime(state);
      if (runtime.registryError) throw new Error(runtime.registryError);
      const descriptors = runtime.descriptors;
      assertValidCalculationDefinition(field.calculation, {
        fields: descriptors.map((descriptor) => ({
          key: descriptor.definition.key,
          label: descriptor.definition.label,
          owner: descriptor.owner,
          type: descriptor.definition.type,
          enabled: descriptor.definition.enabled,
          calculation: descriptor.definition.calculation,
          factorApplicability: descriptor.binding.factorApplicability,
        })),
        currentFieldKey: field.key,
      });
    } catch (error) {
      return errorResult(
        field,
        error instanceof Error ? error.message : '计算定义无效',
      );
    }
  }
  if (
    field.calculation &&
    field.governance.kind === 'custom' &&
    frozenQuote &&
    frozenQuote.status !== 'draft'
  )
    return errorResult(field, '历史报价缺少冻结计算事实');
  if (field.governance.kind === 'custom') {
    const owner = field.groupKey.replace(/^business:/, '') as CustomFieldOwner;
    if (owner !== context.owner)
      return errorResult(field, '字段归属与对象不一致');
    if (field.calculation) {
      const values: Record<string, unknown> = {};
      const fieldTypes: Record<string, CalculationOperandValueType> = {};
      const missing: string[] = [];
      for (const operand of field.calculation.operands) {
        if (operand.kind === 'constant') continue;
        const resolved = resolveUnifiedFieldValue(
          state,
          operand.fieldKey,
          context,
          [...stack, fieldKey],
          runtime,
        );
        if (resolved.status !== 'value' || typeof resolved.value !== 'number') {
          missing.push(...(resolved.missingFieldKeys ?? [operand.fieldKey]));
          continue;
        }
        const type = calculationType(resolved.type);
        if (!type)
          return errorResult(field, `因子类型不可计算：${operand.fieldKey}`);
        values[operand.fieldKey] = { value: resolved.value, type };
        fieldTypes[operand.fieldKey] = type;
      }
      if (missing.length)
        return errorResult(field, '上游计算因子缺失', [...new Set(missing)]);
      const result: CalculationEvaluationResult = evaluateCalculation(
        field.calculation,
        values,
        { fieldTypes },
      );
      return result.ok && result.value !== undefined
        ? {
            key: field.key,
            type: field.type,
            status: 'value',
            value: result.value,
            source: 'calculated',
            definitionVersion: field.governance.definitionVersion,
          }
        : errorResult(
            field,
            result.error ?? '计算失败',
            result.missingFieldKeys,
          );
    }
    if (!context.fieldValuePort)
      return errorResult(field, '企业字段值端口未注入');
    const value = context.fieldValuePort.read(
      state,
      owner,
      context.entityId,
      field.key,
      { quoteId: context.quoteId, quoteVersion: context.quoteVersion },
    );
    return value === undefined
      ? {
          key: field.key,
          type: field.type,
          status: 'missing',
          source: 'enterprise',
          definitionVersion: field.governance.definitionVersion,
        }
      : {
          key: field.key,
          type: field.type,
          status: 'value',
          value: value as ResolvedTypedFieldValue['value'],
          source: 'enterprise',
          definitionVersion: field.governance.definitionVersion,
        };
  }
  const fragmentLine = quoteLineForContext(state, context)?.item;
  if (
    fragmentLine?.sourceFragment &&
    [
      'scheme_product_subtotal',
      'package_item_monthly_subtotal',
      'package_discounted_subtotal',
      'product_final_discount_price',
      'scheme_discount',
      'package_discounted_unit_price',
    ].includes(field.key)
  ) {
    const value = explicitSystemValue(state, field.key, context);
    return typeof value === 'number' && Number.isFinite(value)
      ? {
          key: field.key,
          type: field.type,
          status: 'value',
          value,
          source: 'calculated',
          definitionVersion: field.governance.definitionVersion,
        }
      : {
          key: field.key,
          type: field.type,
          status: 'missing',
          source: 'system',
          definitionVersion: field.governance.definitionVersion,
        };
  }
  if (field.calculation?.resolverRef) {
    const system = systemCalculationContext(state, field, context);
    const auditedResult = system?.audit
      ? evaluateSystemCalculationWithFactors(
          field.calculation.resolverRef,
          system,
        )
      : undefined;
    const result =
      auditedResult ?? evaluateCalculation(field.calculation, {}, { system });
    return result.ok && result.value !== undefined
      ? {
          key: field.key,
          type: field.type,
          status: 'value',
          value: result.value,
          source: 'calculated',
          definitionVersion: field.governance.definitionVersion,
          ...(auditedResult?.factorFacts
            ? { factorFacts: auditedResult.factorFacts }
            : {}),
        }
      : errorResult(
          field,
          result.error ?? '系统计算失败',
          result.missingFieldKeys,
        );
  }
  const value = explicitSystemValue(state, field.key, context);
  return value === undefined ||
    (typeof value === 'number' && !Number.isFinite(value))
    ? {
        key: field.key,
        type: field.type,
        status: 'missing',
        source: 'system',
        definitionVersion: field.governance.definitionVersion,
      }
    : {
        key: field.key,
        type: field.type,
        status: 'value',
        value,
        source: 'system',
        definitionVersion: field.governance.definitionVersion,
      };
}

export interface QuoteFieldValueResolutionOptions {
  placement: QuoteFieldPlacement;
  item?: QuoteItem;
  fieldValuePort: UnifiedFieldValuePort;
  runtime?: QuoteFieldResolutionRuntime;
}

/** Resolve a pooled field against the source object bound by the Registry.
 * This is the only quote consumer entry point for referenced enterprise
 * fields; it never treats a source field as a quote-line field by default. */
export function resolveQuoteFieldValue(
  state: CpqState,
  fieldKey: string,
  quote: Quote,
  options: QuoteFieldValueResolutionOptions,
): ResolvedTypedFieldValue {
  const runtime =
    currentResolutionRuntime(state, options.runtime) ??
    createQuoteFieldResolutionRuntime(state);
  let descriptor: UnifiedFieldDescriptor | undefined;
  try {
    if (runtime.registryError) throw new Error(runtime.registryError);
    descriptor = runtime.descriptorsByKey.get(fieldKey);
  } catch (error) {
    return {
      key: fieldKey,
      type: 'number',
      status: 'error',
      error: error instanceof Error ? error.message : '统一字段注册表无效',
    };
  }
  if (!descriptor) {
    return {
      key: fieldKey,
      type: 'number',
      status: 'error',
      error: `字段定义不存在：${fieldKey}`,
    };
  }
  const { definition, owner, binding } = descriptor;
  if (
    definition.governance.kind === 'custom' &&
    binding.quotePlacement !== options.placement
  )
    return errorResult(definition, '字段报价落点与统一注册绑定不一致');
  if (
    quote.status !== 'draft' &&
    definition.governance.kind === 'custom' &&
    owner !== 'quote-line'
  ) {
    const snapshot =
      owner === 'customer'
        ? quote.sourceFieldValueSnapshot
        : options.item?.sourceFieldValueSnapshot;
    if (!snapshot || !Object.hasOwn(snapshot, fieldKey))
      return errorResult(definition, '历史报价缺少字段源值快照');
    const value = snapshot[fieldKey];
    return value === null
      ? {
          key: fieldKey,
          type: definition.type,
          status: 'missing',
          source: 'enterprise',
          definitionVersion: definition.governance.definitionVersion,
        }
      : {
          key: fieldKey,
          type: definition.type,
          status: 'value',
          value,
          source: 'enterprise',
          definitionVersion: definition.governance.definitionVersion,
        };
  }

  let context: UnifiedFieldResolutionContext | undefined;
  if (definition.governance.kind === 'custom') {
    if (owner === 'quote-line') {
      const item = options.item;
      if (!item) return errorResult(definition, '报价行字段缺少明确报价行');
      context = {
        owner,
        entityId: item.itemId,
        quoteId: quote.quoteId,
        quoteVersion: quote.version,
        item,
        fieldValuePort: options.fieldValuePort,
      };
    } else if (owner === 'device') {
      const item = options.item;
      if (!item || item.sourceType === 'temporary' || !item.deviceId)
        return errorResult(definition, '报价行没有可绑定的设备实体');
      context = {
        owner,
        entityId: item.deviceId,
        quoteId: quote.quoteId,
        quoteVersion: quote.version,
        item,
        fieldValuePort: options.fieldValuePort,
      };
    } else if (owner === 'package') {
      const entityId = options.item?.packageId;
      if (!entityId)
        return errorResult(definition, '报价行没有可绑定的套餐实体');
      context = {
        owner,
        entityId,
        quoteId: quote.quoteId,
        quoteVersion: quote.version,
        item: options.item,
        fieldValuePort: options.fieldValuePort,
      };
    } else if (owner === 'plan') {
      const entityId = options.item?.planId;
      if (!entityId)
        return errorResult(definition, '报价行没有可绑定的方案实体');
      context = {
        owner,
        entityId,
        quoteId: quote.quoteId,
        quoteVersion: quote.version,
        item: options.item,
        fieldValuePort: options.fieldValuePort,
      };
    } else if (owner === 'customer') {
      const entityId = quote.customer.customerId;
      if (!entityId) return errorResult(definition, '报价没有可绑定的客户实体');
      context = {
        owner,
        entityId,
        quoteId: quote.quoteId,
        quoteVersion: quote.version,
        item: options.item,
        fieldValuePort: options.fieldValuePort,
      };
    } else {
      return errorResult(definition, '报价抬头不开放企业自定义字段');
    }
  } else if (options.placement === 'quote-header') {
    context = {
      owner: 'quote-header',
      entityId: quote.quoteId,
      quoteId: quote.quoteId,
      quoteVersion: quote.version,
      fieldValuePort: options.fieldValuePort,
    };
  } else {
    const item = options.item;
    if (!item) return errorResult(definition, '报价列缺少明确报价行');
    context = {
      owner: 'quote-line',
      entityId: item.itemId,
      quoteId: quote.quoteId,
      quoteVersion: quote.version,
      item,
      fieldValuePort: options.fieldValuePort,
    };
  }
  return resolveUnifiedFieldValue(state, fieldKey, context, [], runtime);
}

/**
 * The package row is a projection of already frozen quote lines.  It never
 * re-reads package/device/plan masters, and treats a missing or non-numeric
 * child value as unavailable rather than silently producing a partial total.
 */
export function resolvePackageQuoteSubtotal(
  state: CpqState,
  fieldKey: string,
  quote: Quote,
  items: readonly QuoteItem[],
  fieldValuePort: UnifiedFieldValuePort,
  runtime?: QuoteFieldResolutionRuntime,
): ResolvedTypedFieldValue {
  const values = items.map((item) =>
    resolveQuoteFieldValue(state, fieldKey, quote, {
      placement: 'quote-line',
      item,
      fieldValuePort,
      runtime,
    }),
  );
  const first = values[0];
  if (!first) {
    return {
      key: fieldKey,
      type: 'money',
      status: 'missing',
      error: '套餐汇总缺少报价行',
    };
  }
  if (values.some((value) => value.status !== 'value'))
    return { ...first, status: 'missing', value: undefined };
  const numericValues = values.map((value, index) => {
    const amount = Number(value.value);
    // The device row shows a retail unit price; source and quote totals show
    // the extended amount for the actual quote quantity.
    return fieldKey === 'retail_price_tax13'
      ? Math.round(amount * 100) * items[index].quantity
      : fieldKey === 'dynamic_cost_subtotal'
        ? Math.round(amount * 100)
        : amount;
  });
  if (numericValues.some((value) => !Number.isFinite(value)))
    return {
      ...first,
      status: 'error',
      value: undefined,
      error: '套餐汇总值无效',
    };
  return {
    ...first,
    status: 'value',
    value:
      numericValues.reduce((sum, value) => sum + value, 0) /
      (fieldKey === 'retail_price_tax13' || fieldKey === 'dynamic_cost_subtotal'
        ? 100
        : 1),
  };
}

function formatQuoteMoney(value: number): string {
  return Number.isFinite(value)
    ? `¥${value.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '待维护';
}

export function formatQuoteFieldValue(
  field:
    | Pick<QuoteFieldDefinitionRecord, 'type'>
    | Pick<QuoteColumnSnapshot, 'type'>,
  resolved: ResolvedTypedFieldValue,
): string {
  if (resolved.status !== 'value' || resolved.value === undefined) return '-';
  if (Array.isArray(resolved.value)) return resolved.value.join('；') || '-';
  if (resolved.value === '') return '-';
  if (field.type === 'money') return formatQuoteMoney(Number(resolved.value));
  if (field.type === 'percent') return `${resolved.value}%`;
  if (field.type === 'boolean') return resolved.value ? '是' : '否';
  return String(resolved.value);
}

export function resolveQuoteHeaderFieldDisplay(
  field: QuoteColumnSnapshot,
  quote: Quote,
  businessModes?: CpqState['catalog']['businessModes'],
): string {
  const values: Record<string, string | number> = {
    quote_title: quote.title,
    customer_name: quote.customer.name,
    customer_contact: quote.customer.contact,
    customer_phone: quote.customer.phone,
    valid_until: quote.validUntil,
    business_mode: businessModes
      ? currentBusinessModeLabel(
          businessModes,
          quote.businessModeCode,
          quote.businessModeLabelSnapshot,
        )
      : quote.businessModeLabelSnapshot,
    deposit: quote.deposit,
    overall_discount: quote.overallDiscount,
  };
  const value = values[field.key];
  if (value === undefined || value === '') return '-';
  if (field.type === 'money') return formatQuoteMoney(Number(value));
  if (field.type === 'percent') return `${value}%`;
  return String(value);
}

const DEVICE_SNAPSHOT_FIELDS = new Set([
  'jky_code',
  'retail_price_tax13',
  'rental_price',
  'tax_inclusive_plan_price',
  'device_unit_price',
  'device_untaxed_plan_price',
  'device_compensation_price',
  'rental_subtotal',
  'unit_price_subtotal',
  'purchase_final_price_tax13',
  'purchase_final_price_ex_tax13',
  'dynamic_cost',
  'dynamic_cost_subtotal',
  'device_image',
  'device_category',
  'device_attribute',
  'procurement_attribute',
  'device_weight',
  'warehouse_stock',
]);

// 现代实例读取正式方案专属价格快照；旧冻结实例没有该快照，继续按
// 当时的计价视图展示，不回写也不重算历史发布金额。
function occurrenceDisplayUnitPrice(item: QuoteItem): number {
  if (item.planProductPriceSnapshot !== undefined)
    return item.planProductPriceSnapshot;
  const tuple = item.formalPlanOccurrence;
  if (!tuple) return Number.NaN;
  if (tuple.pricingView === 'purchase')
    return tuple.purchaseUnitPriceCents === null
      ? Number.NaN
      : tuple.purchaseUnitPriceCents / 100;
  return item.packageItemPriceSnapshot === undefined
    ? Number.NaN
    : item.packageItemPriceSnapshot * Number(tuple.pricingCoefficient);
}

function occurrenceDisplaySubtotal(item: QuoteItem): number {
  if (item.planProductSubtotalSnapshot !== undefined)
    return item.planProductSubtotalSnapshot;
  const cents = item.formalPlanOccurrence?.sourceLineCents;
  return Number.isSafeInteger(cents) ? (cents as number) / 100 : Number.NaN;
}

/** A frozen source ratio may exceed 100%; exact quote prices remain the facts.
 * Only lines without these facts use the validated catalogue formula fallback. */
function quotePlanDisplayDiscount(item: QuoteItem): number | undefined {
  const source = item.sourceFragment
    ? item.fragmentAllocation?.sourceExclusiveDiscountPercent == null
      ? undefined
      : Number(item.fragmentAllocation.sourceExclusiveDiscountPercent)
    : item.formalPlanOccurrence
      ? Number(item.formalPlanOccurrence.pricingCoefficient) * 100
      : item.planId
        ? item.planDiscountSnapshot
        : 100;
  return item.sourceFragment &&
    source === undefined &&
    quoteLocalPricing(item).exclusiveDiscountPercent === undefined
    ? undefined
    : quoteLocalExclusiveDiscount(item, source ?? 100);
}

function hasFrozenPlanPrice(item: QuoteItem): boolean {
  return (
    quoteLocalExclusiveLineCents(item) !== undefined ||
    (item.sourceType === 'plan' &&
      (item.planProductPriceSnapshot !== undefined ||
        item.planProductSubtotalSnapshot !== undefined))
  );
}

function hasAboveBasisFrozenPlanPrice(item: QuoteItem): boolean {
  const basis = item.packageItemPriceSnapshot;
  if (item.sourceType !== 'plan') return false;
  if ((quotePlanDisplayDiscount(item) ?? 0) > 100 && hasFrozenPlanPrice(item))
    return true;
  if (basis === undefined) return false;
  return (
    (item.planProductPriceSnapshot !== undefined &&
      item.planProductPriceSnapshot > basis) ||
    (item.planProductSubtotalSnapshot !== undefined &&
      item.planProductSubtotalSnapshot > basis * item.quantity)
  );
}

function quotePlanDisplayUnitPrice(
  item: QuoteItem,
  discount: number | undefined,
): number | undefined {
  const local = quoteLocalExclusiveLineCents(item);
  if (local !== undefined) return local / 100 / item.quantity;
  if (
    quoteDiscountParticipation(item) === 'excluded' &&
    item.packageItemPriceSnapshot !== undefined
  )
    return item.packageItemPriceSnapshot;
  if (item.formalPlanOccurrence) return occurrenceDisplayUnitPrice(item);
  if (item.planProductPriceSnapshot !== undefined)
    return item.planProductPriceSnapshot;
  if (item.planProductSubtotalSnapshot !== undefined)
    return item.planProductSubtotalSnapshot / item.quantity;
  if (item.sourceFragment) {
    const cents = item.fragmentAllocation?.allocatedRentalLineCents;
    return cents === undefined ? undefined : cents / 100 / item.quantity;
  }
  return item.packageItemPriceSnapshot === undefined || discount === undefined
    ? undefined
    : planProductPrice(item.packageItemPriceSnapshot, discount);
}

function quotePlanDisplaySubtotal(
  item: QuoteItem,
  discount: number | undefined,
): number | undefined {
  const local = quoteLocalExclusiveLineCents(item);
  if (local !== undefined) return local / 100;
  if (item.sourceFragment) {
    const cents = item.fragmentAllocation?.allocatedRentalLineCents;
    return cents === undefined ? undefined : cents / 100;
  }
  if (item.formalPlanOccurrence) return occurrenceDisplaySubtotal(item);
  if (item.planProductSubtotalSnapshot !== undefined)
    return item.planProductSubtotalSnapshot;
  const quantity = item.packageRelationQuantitySnapshot ?? item.quantity;
  if (item.planProductPriceSnapshot !== undefined)
    return item.planProductPriceSnapshot * quantity;
  return item.packageItemPriceSnapshot === undefined || discount === undefined
    ? undefined
    : planProductSubtotal(item.packageItemPriceSnapshot, discount, quantity);
}

/** Optional device-master prices are formally connected and manually
 * maintained. A missing value means the bound revision still needs data. */
function optionalDeviceMasterPrice(value: unknown): number | '待维护' {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : '待维护';
}

/** Existing 3.x system-field display semantics, kept out of the React tree. */
export function resolveQuoteSystemFieldDisplay(
  field: QuoteColumnSnapshot,
  item: QuoteItem,
  quote: Quote,
  device?: DeviceCatalogRecord,
): string {
  const availability = quoteFieldAvailabilityForItem(field.key, item);
  if (availability !== 'available')
    return quoteFieldAvailabilityLabel(availability);
  if (DEVICE_SNAPSHOT_FIELDS.has(field.key) && !item.deviceFieldSnapshot)
    return '未接入';
  // Device is accepted for call-site compatibility only. All historical facts
  // below come from the frozen quote line, never the live catalogue record.
  void device;
  const rentalPrice = quoteLocalRentalPrice(item);
  const monthlySubtotal =
    rentalPrice === undefined
      ? undefined
      : quoteMonthlySubtotal(rentalPrice, item.quantity);
  const discounted = lineTotal(item);
  const packageItems = quotePackageGroupItems(quote, item);
  const isPackageSummaryRow = packageItems[0]?.itemId === item.itemId;
  const planDiscount = quotePlanDisplayDiscount(item);
  const deviceSnapshot = item.deviceFieldSnapshot;
  const values: Record<string, string | number> = {
    rental_price: rentalPrice ?? '未接入',
    device_unit_price: deviceSnapshot?.unitPrice ?? '未接入',
    device_untaxed_plan_price: deviceSnapshot?.untaxedPlanPrice ?? '未接入',
    device_compensation_price: deviceSnapshot?.compensationPrice ?? '未接入',
    rental_subtotal:
      quoteLocalExclusiveLineCents(item) === undefined
        ? (monthlySubtotal ?? '未接入')
        : (quoteLocalExclusiveLineCents(item) as number) / 100,
    unit_price_subtotal: deviceSnapshot
      ? (deviceSnapshot.unitPrice ?? 0) * item.quantity
      : '未接入',
    scheme_discount: planDiscount ?? '未接入',
    item_discount: item.discount,
    product_final_discount_price: discounted,
    jky_code: deviceSnapshot ? (deviceSnapshot.jkyCode ?? '') : '未接入',
    device_image: deviceSnapshot?.image ?? '未接入',
    device_name: item.quoteDisplayName?.trim() || item.name,
    device_sku: item.sku,
    quantity: item.quantity,
    warehouse_stock: deviceSnapshot?.stockByWarehouse
      ? Object.entries(deviceSnapshot.stockByWarehouse)
          .map(([warehouse, stock]) => `${warehouse}:${stock}`)
          .join('；')
      : '未接入',
    replacement_device: item.replacementDeviceId ?? '-',
    device_category: deviceSnapshot?.category ?? '未接入',
    device_attribute: deviceSnapshot?.deviceAttribute ?? '未接入',
    quote_remark: item.remark ?? '',
    procurement_attribute: deviceSnapshot?.procurementAttribute ?? '未接入',
    device_weight: deviceSnapshot?.weight ?? '未接入',
    // Retail and plan prices are frozen device inputs. Purchase-final fields
    // below are quote-line results and never read the retired manual finals.
    retail_price_tax13: deviceSnapshot
      ? (deviceSnapshot.retailPriceTax13 ?? '待维护')
      : '未接入',
    purchase_final_price_tax13: optionalDeviceMasterPrice(
      purchaseFinalPriceTax13(item, quote.businessModeCode),
    ),
    purchase_final_price_ex_tax13: optionalDeviceMasterPrice(
      purchaseFinalPriceExTax13(item, quote.businessModeCode),
    ),
    tax_inclusive_plan_price: optionalDeviceMasterPrice(
      deviceSnapshot?.taxInclusivePlanPrice,
    ),
    dynamic_cost: deviceSnapshot
      ? (quoteLocalDynamicCost(item) ?? '未接入')
      : '未接入',
    dynamic_cost_subtotal: deviceSnapshot
      ? (quoteLocalDynamicCostLine(item) ?? '未接入')
      : '未接入',
    package_item_price: item.packageItemPriceSnapshot ?? '未接入',
    package_item_monthly_subtotal: item.sourceFragment
      ? item.fragmentAllocation?.allocatedPackageOriginalLineCents === undefined
        ? NaN
        : item.fragmentAllocation.allocatedPackageOriginalLineCents / 100
      : item.packageItemPriceSnapshot !== undefined &&
          item.packageRelationQuantitySnapshot !== undefined
        ? packageItemMonthlySubtotal({
            packageItemPrice: item.packageItemPriceSnapshot,
            quantity: item.packageRelationQuantitySnapshot,
          })
        : '未接入',
    scheme_product_price:
      quotePlanDisplayUnitPrice(item, planDiscount) ?? '未接入',
    scheme_product_subtotal:
      quotePlanDisplaySubtotal(item, planDiscount) ?? '未接入',
    scheme_package_price: isPackageSummaryRow
      ? (item.planSubtotalSnapshot ?? item.planPackagePriceSnapshot ?? '未接入')
      : '-',
    package_original_price_tax13: isPackageSummaryRow
      ? (item.packageMonthlySubtotalSnapshot ??
        item.packageOriginalPriceSnapshot ??
        '未接入')
      : '-',
    package_discount_quote_tax13:
      isPackageSummaryRow && packageItems.length > 0
        ? packageItems.some(hasAboveBasisFrozenPlanPrice)
          ? packageItems.reduce((total, row) => total + lineTotal(row), 0)
          : packageDiscountedQuote(packageItems)
        : '-',
    package_discounted_unit_price:
      item.sourceFragment ||
      item.formalPlanOccurrence ||
      hasAboveBasisFrozenPlanPrice(item)
        ? lineTotal(item) / item.quantity
        : item.packageItemPriceSnapshot !== undefined &&
            planDiscount !== undefined
          ? packageDiscountedUnitPrice(
              item.packageItemPriceSnapshot,
              planDiscount,
              item.discount,
            )
          : '未接入',
    package_discounted_subtotal: rentalLineTotal(item, quote.businessModeCode),
    valid_until: quote.validUntil || '-',
  };
  const value = values[field.key] ?? '-';
  if (value === '-') return '-';
  if (value === '未接入') return '未接入';
  if (value === '待维护') return '待维护';
  if (field.type === 'money') {
    return formatQuoteMoney(Number(value));
  }
  if (field.type === 'percent') return `${value}%`;
  return String(value);
}
